import { HD, Mnemonic, PrivateKey } from "@bsv/sdk";
import {
	isSeedIdentityBackup,
	validateIdentityBackup,
} from "@/lib/bap/identity-backup";
import { arrayBufferToBase64, base64ToArrayBuffer } from "./encryption";

export interface VaultEnvelope {
	version: 1;
	keyId: string;
	nonce: string;
	ciphertext: string;
}

export async function vaultKeyFromBytes(bytes: ArrayBuffer) {
	if (bytes.byteLength !== 32) {
		throw new Error("Invalid backup unlock key");
	}
	const digest = await crypto.subtle.digest("SHA-256", bytes);
	const keyId = Array.from(new Uint8Array(digest), (b) =>
		b.toString(16).padStart(2, "0")
	).join("");
	const key = await crypto.subtle.importKey("raw", bytes, "AES-GCM", false, [
		"encrypt",
		"decrypt",
	]);
	return { key, keyId, bytes };
}

/** Derive only from recovery authority, never mutable profile data or passwords. */
export async function deriveVaultKey(value: unknown) {
	const backup = validateIdentityBackup(value);
	let kind: string;
	let secret: Uint8Array<ArrayBuffer>;
	if (isSeedIdentityBackup(backup)) {
		kind = "sigma-seed-v1";
		secret = new Uint8Array(Mnemonic.fromString(backup.mnemonic).toSeed(""));
	} else if ("xprv" in backup) {
		kind = "bap-xprv";
		secret = new TextEncoder().encode(HD.fromString(backup.xprv).toString());
	} else {
		kind = "rootPk" in backup ? "bap-root" : "bap-account";
		secret = new Uint8Array(
			PrivateKey.fromWif(
				"rootPk" in backup ? backup.rootPk : backup.wif
			).toArray("be", 32)
		);
	}
	const source = await crypto.subtle.importKey("raw", secret, "HKDF", false, [
		"deriveBits",
	]);
	const bytes = await crypto.subtle.deriveBits(
		{
			name: "HKDF",
			hash: "SHA-256",
			salt: new Uint8Array(0),
			info: new TextEncoder().encode(`sigma/prf-vault/v1/${kind}`),
		},
		source,
		256
	);
	secret.fill(0);
	return vaultKeyFromBytes(bytes);
}

function associatedData(keyId: string, context: string) {
	return new TextEncoder().encode(
		JSON.stringify(["sigma-prf-vault", 1, keyId, context])
	);
}

export async function sealVaultData(
	plaintext: string,
	key: CryptoKey,
	keyId: string,
	context: string
): Promise<string> {
	const nonce = crypto.getRandomValues(new Uint8Array(12));
	const ciphertext = await crypto.subtle.encrypt(
		{
			name: "AES-GCM",
			iv: nonce,
			additionalData: associatedData(keyId, context),
		},
		key,
		new TextEncoder().encode(plaintext)
	);
	return JSON.stringify({
		version: 1,
		keyId,
		nonce: arrayBufferToBase64(nonce.buffer),
		ciphertext: arrayBufferToBase64(ciphertext),
	} satisfies VaultEnvelope);
}

export async function openVaultData(
	encoded: string,
	key: CryptoKey,
	keyId: string,
	context: string
): Promise<string> {
	const envelope = JSON.parse(encoded) as VaultEnvelope;
	if (envelope.version !== 1 || envelope.keyId !== keyId) {
		throw new Error("Backup unlock needs to be enabled again");
	}
	const plain = await crypto.subtle.decrypt(
		{
			name: "AES-GCM",
			iv: base64ToArrayBuffer(envelope.nonce),
			additionalData: associatedData(keyId, context),
		},
		key,
		base64ToArrayBuffer(envelope.ciphertext)
	);
	return new TextDecoder().decode(plain);
}

export function credentialContext(
	credentialId: string,
	prfSalt: string
): string {
	return JSON.stringify(["credential", credentialId, prfSalt]);
}
